import { useNavigate, useParams } from 'react-router-dom';
import { INSPIRATION, COUNTRY_TABS, REGIONS } from '../data/site';
import { useSite } from '../data/SiteContext';
import NotFoundBlock from './NotFoundBlock';

// Tab key → the guide entity's flat field prefix (same mapping the regions pages use).
const FIELD = { 'overview':'overview', 'best-time':'bestTime', 'resorts':'resorts', 'hotels':'hotels', 'excursions':'excursions', 'currency':'currency', 'weather':'weather', 'visa':'visa', 'tips':'tips', 'faq':'faq' };

/** Country guides carry the real content; their photo comes from the destination in that country. */
const useGuides = () => {
  const { countryGuides, dests } = useSite();
  const norm = (s) => (s || '').toLowerCase().trim();
  const photoFor = (countryName) => dests.find(d => norm(d.country) === norm(countryName))?.img || null;
  const active = countryGuides.filter(g => g.isActive).sort((a, b) => a.countryName.localeCompare(b.countryName));
  return { active, photoFor };
};

export function GuidesHub() {
  const navigate = useNavigate();
  const { loading } = useSite();
  const { active, photoFor } = useGuides();
  return (
    <div className="wrap" style={{ paddingTop:40, paddingBottom:72 }}>
      <div className="kicker">Travel Guides</div>
      <h1 className="page-h1">Know before you go.</h1>
      <p className="page-sub">A guide for every country we sell — seasons, where to base yourself, money, entry rules and what to book ahead. Written by the specialists who plan these trips.</p>
      {active.length === 0 ? (
        <p style={{ color:'#5a726f', fontSize:16 }}>{loading ? 'Loading…' : 'Guides are being written — ask us anything in the meantime.'}</p>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:22, marginBottom:44 }}>
          {active.map(g => (
            <div key={g.key} className="card-cool lift3" onClick={() => navigate('/guides/' + g.key)} style={{ cursor:'pointer', borderRadius:20, overflow:'hidden', boxShadow:'0 10px 26px rgba(16,90,90,0.1)' }}>
              <div style={{ height:160 }}><div className="bgimg" style={{ backgroundImage:`url(${photoFor(g.countryName)})` }}></div></div>
              <div style={{ padding:'16px 18px' }}>
                <div className="serif" style={{ fontSize:21 }}>{g.countryName}</div>
                <div style={{ fontSize:12.5, color:'#5a726f', marginTop:4 }}>Best time · Resorts · Money · Visa · Tips</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ padding:32, borderRadius:26, background:'linear-gradient(135deg,rgba(31,181,106,0.12),rgba(14,138,156,0.1))', border:'1px solid #d8efe8', marginBottom:36 }}>
        <h2 className="serif" style={{ fontSize:'clamp(26px,3.4vw,36px)', margin:'0 0 6px' }}>Inspiration Hub</h2>
        <p style={{ color:'#5a726f', fontSize:15.5, margin:'0 0 20px' }}>Not sure where yet? Start with a feeling.</p>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {INSPIRATION.map(t => (
            <div key={t} className="chip">{t}</div>
          ))}
        </div>
      </div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap', padding:'28px 32px', borderRadius:24, background:'#0c2b2f', color:'#fff' }}>
        <div>
          <div className="serif" style={{ fontSize:28 }}>Travel Journal</div>
          <div style={{ fontSize:15, opacity:.8, marginTop:4 }}>Long-form stories, itineraries and honest advice.</div>
        </div>
        <span className="btn btn-yellow" onClick={() => navigate('/blog')}>Read the journal →</span>
      </div>
    </div>
  );
}

export function GuideDetail() {
  const navigate = useNavigate();
  const { contact, loading } = useSite();
  const { active, photoFor } = useGuides();
  const { key } = useParams();
  const guide = active.find(g => g.key === key);

  if (!guide) {
    if (loading) return <div className="wrap" style={{ paddingTop:80, paddingBottom:120, textAlign:'center', color:'#5a726f' }}>Loading…</div>;
    return <NotFoundBlock title="We don't have that guide" message="The link may be out of date. Browse the full set of country guides instead." backLabel="← All guides" backTo="/guides" />;
  }

  // Only sections the operator has actually written are rendered.
  const sections = COUNTRY_TABS.map(([k, label]) => {
    const f = FIELD[k];
    const text = guide[f + 'Text'];
    const items = (guide[f + 'Items'] || '').split('\n').map(s => s.trim()).filter(Boolean);
    return (text || items.length) ? { key: k, label, text, items } : null;
  }).filter(Boolean);

  const sources = (guide.sourceReferences || '').split('\n').map(s => s.trim()).filter(s => /^https?:\/\//.test(s));
  const region = REGIONS.find(r => r.countries.some(c => c.name.toLowerCase() === guide.countryName.toLowerCase()));
  const country = region?.countries.find(c => c.name.toLowerCase() === guide.countryName.toLowerCase());
  const enquiry = `${contact.waLink}?text=${encodeURIComponent(`Hi! I'd like to plan a trip to ${guide.countryName}.`)}`;

  return (
    <div>
      <section style={{ position:'relative', minHeight:340, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
        <div className="cover bgimg" style={{ backgroundImage:`url(${photoFor(guide.countryName)})` }}></div>
        <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0.2),rgba(6,40,45,0.82))' }}></div>
        <div style={{ position:'relative', maxWidth:900, margin:'0 auto', padding:'0 24px 40px', width:'100%', color:'#fff' }}>
          <span onClick={() => navigate('/guides')} style={{ cursor:'pointer', color:'rgba(255,255,255,0.9)', fontWeight:700, fontSize:14 }}>← All guides</span>
          <h1 className="serif" style={{ fontSize:'clamp(34px,5vw,56px)', margin:'12px 0 0', letterSpacing:'-0.5px' }}>{guide.countryName}</h1>
          <div style={{ fontSize:15, opacity:.9, marginTop:6 }}>The complete travel guide</div>
        </div>
      </section>

      <div style={{ maxWidth:820, margin:'0 auto', padding:'36px 24px 72px' }}>
        {/* Jump links — the guide is long by design. */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:30 }}>
          {sections.map(s => (
            <a key={s.key} href={`#${s.key}`} className="chip" style={{ textDecoration:'none' }}>{s.label}</a>
          ))}
        </div>

        {sections.map(s => (
          <section key={s.key} id={s.key} style={{ marginBottom:34, scrollMarginTop:100 }}>
            <h2 className="serif" style={{ fontSize:28, margin:'0 0 12px' }}>{s.label}</h2>
            {s.text && <p style={{ fontSize:16.5, lineHeight:1.75, color:'#31474a', margin:'0 0 14px' }}>{s.text}</p>}
            {s.items.length > 0 && (
              <div style={{ display:'grid', gap:9 }}>
                {s.items.map(i => (
                  <div key={i} className="card-cool" style={{ display:'flex', alignItems:'flex-start', gap:11, padding:'13px 15px', borderRadius:13, fontSize:14.5, fontWeight:600, lineHeight:1.5 }}>
                    <span style={{ flexShrink:0, width:20, height:20, borderRadius:'50%', background:'linear-gradient(135deg,#26c6a3,#0e8a9c)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:11 }}>✓</span>{i}
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}

        {sources.length > 0 && (
          <div style={{ fontSize:12.5, color:'#8aa19d', lineHeight:1.7, marginBottom:26 }}>
            Information sources:{' '}
            {sources.map((u, ix) => (
              <a key={u} href={u} target="_blank" rel="noreferrer" style={{ color:'#5a8a94' }}>{new URL(u).hostname.replace('www.','')}{ix < sources.length - 1 ? ' · ' : ''}</a>
            ))}
          </div>
        )}

        <div className="card" style={{ padding:26, borderRadius:22, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
          <div>
            <div className="serif" style={{ fontSize:23 }}>Planning {guide.countryName}?</div>
            <div style={{ fontSize:14.5, color:'#5a726f', marginTop:4 }}>Tell us your dates and we'll send a tailored itinerary within 24 hours.</div>
          </div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {region && country && <span className="btn btn-soft" onClick={() => navigate(`/regions/${region.key}/${country.key}`)}>Trips in {guide.countryName}</span>}
            <a href={enquiry} target="_blank" rel="noreferrer" className="btn btn-teal">Ask a specialist →</a>
          </div>
        </div>
      </div>
    </div>
  );
}
